import { ActionMode, ActionSource, HoverType } from 'app/modules/editor/model/actionmode';
import { Point } from 'app/modules/editor/scripts/common';
import { ActionModeService } from 'app/modules/editor/services';
import { findLastIndex } from 'lodash-es';
import type { CanvasOverlay } from './CanvasOverlay';

// TODO: clean up this class' messy communication w/ the overlay directive

/**
 * Helper class that tracks information during morph subpath mode.
 */
export class PairSubPathHelper {
  private readonly actionSource: ActionSource;
  private readonly actionModeService: ActionModeService;

  constructor(private readonly component: CanvasOverlay) {
    this.actionSource = component.actionSource;
    this.actionModeService = component.actionModeService;
  }

  onMouseDown(mouseDown: Point, isShiftOrMetaPressed: boolean) {
    const hitResult = this.performHitTest(mouseDown);

    if (hitResult.isSegmentHit || hitResult.isShapeHit) {
      const hits = hitResult.isShapeHit ? hitResult.shapeHits : hitResult.segmentHits;
      const { subIdx } = this.findHitSubPath(hits);
      this.actionModeService.pairSubPath(subIdx, this.actionSource);
    } else if (!isShiftOrMetaPressed) {
      this.actionModeService.setActionMode(ActionMode.Selection);
    }
  }

  onMouseMove(mouseMove: Point) {
    this.checkForHovers(mouseMove);
    this.component.draw();
  }

  onMouseUp(mouseUp: Point) {
    this.component.draw();
  }

  onMouseLeave() {
    this.component.actionModeService.clearHover();
    this.component.draw();
  }

  private performHitTest(mousePoint: Point) {
    return this.component.performHitTest(mousePoint);
  }

  private checkForHovers(mousePoint: Point) {
    const hitResult = this.performHitTest(mousePoint);
    if (!hitResult.isHit) {
      this.component.actionModeService.clearHover();
    } else if (hitResult.isSegmentHit || hitResult.isShapeHit) {
      const hits = hitResult.isShapeHit ? hitResult.shapeHits : hitResult.segmentHits;
      const { subIdx } = this.findHitSubPath(hits);
      this.component.actionModeService.setHover({
        type: HoverType.SubPath,
        source: this.actionSource,
        subIdx,
      });
    }
  }

  private findHitSubPath(hits: ReadonlyArray<{ subIdx: number }> | undefined) {
    const activePath = this.component.activePath;
    if (!activePath || !hits?.length) {
      // Only the active path is hit tested, and a hit flag is only set if its list isn't empty.
      throw new Error('Expected at least one hit on the active path');
    }
    const infos = hits.map(index => {
      const { subIdx } = index;
      return { subIdx, subPath: activePath.getSubPath(subIdx) };
    });
    const lastSplitIndex = findLastIndex(infos, info => info.subPath.isSplit());
    return infos[lastSplitIndex < 0 ? infos.length - 1 : lastSplitIndex];
  }
}
